import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import APRmemo from "./APRComment.js/APRmemo";
import APRComment from "./APRComment.js/AprComments";
import axios from "../../api/axios";
import { getAttributeValue } from "../../utils/utils";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const PROGRAM_ID = "IhERRdqHsFi";

function parseAmount(value) {
  const parsed = parseFloat(String(value || "0").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseDateValue(value) {
  if (value == null || value === "" || value === "N/A") {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isSelectedYearWithinPeriod(startDateValue, endDateValue, selectedYear) {
  const year = parseInt(selectedYear, 10);
  if (!Number.isFinite(year)) {
    return false;
  }

  const startDate = parseDateValue(startDateValue);
  const endDate = parseDateValue(endDateValue);
  const yearStart = new Date(`${year}-01-01T00:00:00`);
  const yearEnd = new Date(`${year}-12-31T23:59:59`);

  if (startDate && endDate) {
    return startDate <= yearEnd && endDate >= yearStart;
  }

  if (startDate) {
    return startDate <= yearEnd;
  }

  if (endDate) {
    return endDate >= yearStart;
  }

  return false;
}

function buildTableRows(capexTotal, mtbfTotal) {
  const variation = capexTotal - mtbfTotal;

  return [
    { item: "CAPEX throw Forward", amount: capexTotal },
    { item: "MTBF (Ceilings)", amount: mtbfTotal },
    { item: "Variation", amount: variation },
  ];
}

const Table_13 = forwardRef(({ year, district, period, hideTableDis }, ref) => {
  const [tableData, setTableData] = useState([]);

  const selectedYear = useMemo(() => {
    const parsed = parseInt(year, 10);
    return Number.isFinite(parsed) ? parsed : new Date().getFullYear();
  }, [year]);

  useEffect(() => {
    getData();
  }, [district, selectedYear, period]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "Table_13",
      tableData,
    }),
  }));

  async function getData() {
    if (!district) {
      setTableData([]);
      return;
    }

    try {
      const startDate = `${selectedYear - 5}-01-01`;
      const endDate = `${selectedYear + 5}-12-31`;

      const response = await axios.get(
        `/tracker/trackedEntities?orgUnit=${district}&program=${PROGRAM_ID}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
      );

      const instances = response.data.instances || [];
      const matchingRecords = instances.filter((instance) => {
        const startDateValue = getAttributeValue("Start Date", instance);
        const endDateValue = getAttributeValue("End Date", instance);

        return isSelectedYearWithinPeriod(startDateValue, endDateValue, selectedYear);
      });

      if (matchingRecords.length === 0) {
        setTableData([]);
        return;
      }

      const capexTotal = matchingRecords.reduce(
        (sum, record) => sum + parseAmount(getAttributeValue("CAPEX throw Forward", record)),
        0,
      );
      const mtbfTotal = matchingRecords.reduce(
        (sum, record) => sum + parseAmount(getAttributeValue("MTBF (Ceilings)", record)),
        0,
      );

      setTableData(buildTableRows(capexTotal, mtbfTotal));
    } catch (err) {
      console.error("Error loading Table 13 data:", err);
      setTableData([]);
    }
  }

  const chartData = {
    labels: tableData.map((row) => row.item),
    datasets: [
      {
        label: `Amount (GH¢) - ${selectedYear}`,
        data: tableData.map((row) => row.amount),
        backgroundColor: ["#0d6efd", "#fd7e14", "#198754"],
        borderColor: ["#0a58ca", "#e06c00", "#146c43"],
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: `CAPEX Throw Forward and MTBF Analysis, ${selectedYear}`,
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.label}: ${new Intl.NumberFormat("en-GH", {
            style: "currency",
            currency: "GHS",
            minimumFractionDigits: 2,
          }).format(context.parsed.y)}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Amount (GH¢)",
        },
        ticks: {
          callback: (value) => new Intl.NumberFormat("en-GH", {
            style: "currency",
            currency: "GHS",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(value),
        },
      },
    },
  };

  const formatAmount = (value) => value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <div className="col-12">
      <h3>Table 13: Cumulative CAPEX Throw Forward and MTBF Envelope, {selectedYear}</h3>
      <div className="card">
        <div className="card-header" />
        <div className="card-body">
          <APRmemo
            year={year}
            districtId={district}
            tableCommentedId={`table13-${year}`}
            hideTableDis={hideTableDis}
          />
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead style={{ backgroundColor: "#d4edda", fontWeight: "bold", border: "1px solid #000" }}>
                <tr>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Item</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((row, index) => (
                    <tr key={index}>
                      <td style={{ border: "1px solid #000" }}>{row.item}</td>
                      <td style={{ border: "1px solid #000" }}>{formatAmount(row.amount)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="2" style={{ border: "1px solid #000", textAlign: "center" }}>
                      No data available for {selectedYear}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <h4 className="mt-4">Figure 13: CAPEX Throw Forward and MTBF Analysis</h4>
          {tableData.length > 0 ? (
            <Bar data={chartData} options={chartOptions} />
          ) : (
            <p className="text-center">No chart data available for {selectedYear}</p>
          )}

          <p className="mt-2">
            <small>Source: Composite Budget, {selectedYear}</small>
          </p>
          <APRComment
            data={tableData}
            year={year}
            districtId={district}
            tableCommentedId={`table13-${year}`}
            hideTableDis={hideTableDis}
          >
            {({ renderCommentInput, renderCommentList }) => (
              <>
                {renderCommentInput()}
                {renderCommentList()}
              </>
            )}
          </APRComment>
        </div>
      </div>
    </div>
  );
});

export default Table_13;
